import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SettingsCard from '../../organisms/SettingsCard';
import Button from '../../atoms/Button';
import { Heading2 } from '../../atoms/Typography';
import Loading from '../../atoms/Loading';
import Stepper from '../../organisms/Stepper';
import { SETTINGS_STEPS } from '../../../data/settingsSteps';
import { getFormFields } from '../../../redux/complaint/actions';
import Review from './Review';

const FIELD_TYPE_MAP = {
  CUSTOM: 'Custom',
  TEXT: 'Text',
  TEXTAREA: 'Text Area',
  SELECT: 'Select',
  DROPDOWN: 'Dropdown',
  NUMBER: 'Number',
  DATE: 'Date',
  DATETIME: 'Date Time',
  EMAIL: 'Email',
  MULTISELECT: 'MultiSelect',
  CHECKBOX: 'Checkbox',
  RADIO: 'Radio',
  FILE: 'File',
};

const FIELD_ACCESS_MAP = {
  BOTH: 'Both',
  USER: 'User',
  ADMIN: 'Admin',
};

function hideForLabel(step) {
  if (!step) return null;
  if (typeof step === 'string') return step;
  return step.name ?? step.label ?? step.title ?? null;
}

function uniqueValues(values) {
  return [...new Set(values.filter(Boolean))];
}

function optionLabel(value) {
  if (typeof value === 'string') return value;
  return value?.label ?? '';
}

function normalizeFormFields(list = []) {
  const sorted = [...list].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
  );

  return sorted.map((item) => {
    const values = item?.options?.values ?? [];
    const parent = sorted.find((field) => field.id === item.dependsOnFieldId);

    return {
      id: item.id,
      label: item.fieldName ?? item.fieldLabel ?? item.label ?? '',
      fieldLabel: item.fieldName ?? item.fieldLabel ?? item.label ?? '',
      fieldKey: item.fieldKey,
      fieldType: FIELD_TYPE_MAP[item.fieldType] ?? item.fieldType ?? 'Custom',
      required: Boolean(item.required),
      fieldAccess: FIELD_ACCESS_MAP[item.fieldAccess] ?? item.fieldAccess ?? '',
      hideFor: uniqueValues([
        ...(Array.isArray(item.hideFor) ? item.hideFor : []),
        hideForLabel(item.hideForWorkflowStep),
      ]),
      dependsOn: item.dependsOnField?.fieldName ?? parent?.fieldName ?? item.dependsOn ?? '',
      options: values.map(optionLabel).filter(Boolean),
      parentMappings: Object.fromEntries(
        values
          .filter((value) => value && typeof value === 'object' && value.label)
          .map((value) => [value.label, value.parent ?? '']),
      ),
    };
  });
}

export default function ReviewPublishStep({ onPublish: _onPublish, stepMeta, stepperProps }) {
  const dispatch = useDispatch();
  const complaintTypeId = useSelector((store) => store.appDetails?.id);
  const apiFields = useSelector((store) => store.complaintAppComplaint.formFields.data);
  const isLoading = useSelector((store) => store.complaintAppComplaint.formFields.isLoading);
  const error = useSelector((store) => store.complaintAppComplaint.formFields.error);
  const fields = useMemo(() => normalizeFormFields(apiFields), [apiFields]);

  useEffect(() => {
    if (!complaintTypeId) return undefined;
    dispatch(getFormFields(complaintTypeId));
  }, [complaintTypeId, dispatch]);

  return (
    <>
      <Stepper {...stepperProps} steps={SETTINGS_STEPS} />
      <SettingsCard
      className="ca-settings-card--plain"
      title={stepMeta.title}
      description={stepMeta.description}
      showNext={false}
    >
      <div className="ca-review">
        <section className="ca-review__block ca-review__block--last">
          {/* <Heading2>Form Fields</Heading2> */}
          {isLoading ? (
            <Loading text="Loading form fields" />
          ) : error && !fields.length ? (
            <p className="ca-body">{error}</p>
          ) : (
            <Review fields={fields} />
          )}
        </section>

        {/* <div className="ca-workflow__actions">
          <Button onClick={_onPublish}>Publish</Button>
        </div> */}
      </div>
      </SettingsCard>
    </>
  );
}
